import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

// Enregistrement sécurisé du plugin ScrollTrigger
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

/**
 * AnimatedSection - Composant réutilisable d'animation au scroll utilisant GSAP & @gsap/react
 * 
 * @param {React.ReactNode} children - Contenu à animer
 * @param {'fade-up' | 'fade-in' | 'slide-left' | 'slide-right' | 'scale' | 'stagger'} animation - Type d'animation
 * @param {number} delay - Délai avant le démarrage (en secondes)
 * @param {number} duration - Durée de l'animation (en secondes)
 * @param {number} stagger - Délai de décalage entre éléments enfants (si animation === 'stagger')
 * @param {string} [selector] - Sélecteur CSS d'éléments cibles spécifiques dans le container
 * @param {string} start - Point de déclenchement ScrollTrigger (ex: 'top 85%')
 * @param {boolean} once - Si vrai, l'animation ne se joue qu'une seule fois
 * @param {string} className - Classes CSS additionnelles
 * @param {React.ElementType} as - Élément HTML ou composant wrapper (default: 'div')
 */
export default function AnimatedSection({
  children,
  animation = 'fade-up',
  delay = 0,
  duration = 0.8,
  stagger = 0.12,
  selector,
  start = 'top 85%',
  once = true,
  className = '',
  as: Component = 'div',
  ...props
}) {
  const containerRef = useRef(null);

  useGSAP(
    () => {
      // Respect des préférences d'accessibilité (réduction du mouvement)
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      const container = containerRef.current;
      if (!container) return;

      let targetElements;
      if (selector) {
        targetElements = container.querySelectorAll(selector);
      } else if (animation === 'stagger') {
        targetElements = Array.from(container.children);
      } else {
        targetElements = [container];
      }

      if (!targetElements || targetElements.length === 0) return;

      const fromVars = { opacity: 0 };
      const toVars = {
        opacity: 1,
        duration: duration,
        delay: delay,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: container,
          start: start,
          toggleActions: once ? 'play none none none' : 'play reverse play reverse',
        },
      };

      switch (animation) {
        case 'fade-up':
          fromVars.y = 40;
          toVars.y = 0;
          break;
        case 'slide-left':
          fromVars.x = -40;
          toVars.x = 0;
          break;
        case 'slide-right':
          fromVars.x = 40;
          toVars.x = 0;
          break;
        case 'scale':
          fromVars.scale = 0.92;
          fromVars.y = 20;
          toVars.scale = 1;
          toVars.y = 0;
          break;
        case 'stagger':
          fromVars.y = 35;
          toVars.y = 0;
          toVars.stagger = stagger;
          break;
        case 'fade-in':
        default:
          break;
      }

      gsap.fromTo(targetElements, fromVars, toVars);
    },
    { scope: containerRef, dependencies: [animation, delay, duration, stagger, selector, start, once] }
  );

  return (
    <Component ref={containerRef} className={className} {...props}>
      {children}
    </Component>
  );
}
